import React, { useId, useState } from 'react';

import { A11Y_CLICK_KEYS_MAP, mergeClassNameStrings } from '@consumer-frontend/nc-utils';
import { Icon } from '@consumer-frontend/shared-ui';

import { NcStaticBadge, NcStaticBadgeConfigT } from '../NcStaticBadge';

import '@consumer-bootstrap/netcredit.scss';
// eslint-disable-next-line import/order
import '@consumer-bootstrap/brands/netcredit/expandable-list.scss';

export type NcExpandableListItemPropsT = {
  body: string;
  title: string;
  badgeConfig?: NcStaticBadgeConfigT;
  chromaticIgnoreTitle?: boolean;
  className?: string;
  dropdownBody?: React.ReactNode;
};

export type NcExpandableListItemT = React.FC<NcExpandableListItemPropsT>;

export const NcExpandableListItem: NcExpandableListItemT = ({
  badgeConfig,
  body,
  chromaticIgnoreTitle,
  className,
  dropdownBody,
  title,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const keyDownHandler = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if ([A11Y_CLICK_KEYS_MAP.SPACE, A11Y_CLICK_KEYS_MAP.ENTER].includes(event.key)) {
      event.preventDefault();
      setIsDropdownOpen(!isDropdownOpen);
    }
  };

  const clickHandler = () => setIsDropdownOpen((open) => !open);

  const dropdownId = useId();

  const headerClassName = mergeClassNameStrings(
    'expandable-list-item-header',
    !!dropdownBody && 'expandable-list-item-header-clickable',
  );

  return (
    <div className={mergeClassNameStrings('expandable-list-item', className)} role="listitem">
      <div
        className={headerClassName}
        aria-expanded={dropdownBody ? isDropdownOpen : undefined}
        aria-controls={dropdownBody ? dropdownId : undefined}
        role={dropdownBody ? 'button' : undefined}
        tabIndex={dropdownBody ? 0 : undefined}
        onClick={dropdownBody ? clickHandler : undefined}
        onKeyDown={dropdownBody ? keyDownHandler : undefined}
      >
        <div className="expandable-list-item-title-and-body">
          <span
            className="expandable-list-item-title"
            data-chromatic={chromaticIgnoreTitle ? 'ignore' : undefined}
          >
            {title}
          </span>
          <span className="expandable-list-item-body">{body}</span>
        </div>

        <div className="expandable-list-item-icons">
          {badgeConfig && <NcStaticBadge config={badgeConfig} />}
          {dropdownBody && (
            <Icon className="expandable-list-item-dropdown-icon" iconName={isDropdownOpen ? 'chevron-up' : 'chevron-down'} />
          )}
        </div>
      </div>

      {isDropdownOpen && dropdownBody && (
        <div className="expandable-list-item-dropdown" id={dropdownId}>
          <hr aria-hidden="true" className="expandable-list-item-divider" />
          {dropdownBody}
        </div>
      )}
    </div>
  );
};
